import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  title: 'Your notes',
  description: 'Capture what matters, in your own words.',
  access: 'Visible to everyone who can access this meeting.',
  fromChat: 'Added through chat',
  unknownAuthor: 'Former member',
  removeQuestion: 'Remove this note?',
  remove: 'Remove',
  cancel: 'Cancel',
  loadFailed: 'Notes could not be loaded.',
  saveFailed: 'Your note could not be saved. Your text is still here; try again.',
  removeFailed: 'The note could not be removed.',
  fileFailed: 'Choose a .txt or .md file with up to 12,000 characters.',
  label: 'New note',
  placeholder: 'Ideas, decisions, details to remember…',
  shortcut: '⌘ / Ctrl + Enter to save',
  count: '{count} / {max}',
  saving: 'Saving…',
  save: 'Save note',
  add: 'Add a note',
  upload: 'Upload notes',
  chat: 'Add through chat',
  chatPrompt:
    'Help me add a note to this meeting. Ask me what I want to capture, then show me the note for approval before saving it.',
  more: 'Show more notes',
  retry: 'Try again',
  loading: 'Loading notes…',
  saved: 'Note saved',
  views: 'Meeting views',
  canvas: 'Canvas',
  transcript: 'Transcript',
};
type Key = keyof typeof en;
const copy: NamespaceCatalog<Key> = {
  en,
  bg: {
    title: 'Вашите бележки',
    description: 'Запишете важното със свои думи.',
    access: 'Видими за всички с достъп до тази среща.',
    fromChat: 'Добавено чрез чата',
    unknownAuthor: 'Бивш член',
    removeQuestion: 'Да премахна ли тази бележка?',
    remove: 'Премахни',
    cancel: 'Отказ',
    loadFailed: 'Бележките не можаха да се заредят.',
    saveFailed: 'Бележката не можа да се запази. Текстът е тук; опитайте отново.',
    removeFailed: 'Бележката не можа да се премахне.',
    fileFailed: 'Изберете .txt или .md файл с до 12 000 знака.',
    label: 'Нова бележка',
    placeholder: 'Идеи, решения, подробности за запомняне…',
    shortcut: '⌘ / Ctrl + Enter за запазване',
    count: '{count} / {max}',
    saving: 'Запазване…',
    save: 'Запази бележката',
    add: 'Добави бележка',
    upload: 'Качи бележки',
    chat: 'Добави чрез чата',
    chatPrompt:
      'Помогни ми да добавя бележка към тази среща. Попитай какво искам да запиша и покажи бележката за одобрение, преди да я запазиш.',
    more: 'Покажи още бележки',
    retry: 'Опитай отново',
    loading: 'Зареждане на бележки…',
    saved: 'Бележката е запазена',
    views: 'Изгледи на срещата',
    canvas: 'Работно платно',
    transcript: 'Транскрипция',
  },
  cs: {
    title: 'Vaše poznámky',
    description: 'Zachyťte to důležité vlastními slovy.',
    access: 'Viditelné pro všechny s přístupem k této schůzce.',
    fromChat: 'Přidáno přes chat',
    unknownAuthor: 'Bývalý člen',
    removeQuestion: 'Odstranit tuto poznámku?',
    remove: 'Odstranit',
    cancel: 'Zrušit',
    loadFailed: 'Poznámky se nepodařilo načíst.',
    saveFailed: 'Poznámku se nepodařilo uložit. Text zůstává zachován; zkuste to znovu.',
    removeFailed: 'Poznámku se nepodařilo odstranit.',
    fileFailed: 'Vyberte soubor .txt nebo .md s nejvýše 12 000 znaky.',
    label: 'Nová poznámka',
    placeholder: 'Nápady, rozhodnutí, podrobnosti k zapamatování…',
    shortcut: '⌘ / Ctrl + Enter pro uložení',
    count: '{count} / {max}',
    saving: 'Ukládání…',
    save: 'Uložit poznámku',
    add: 'Přidat poznámku',
    upload: 'Nahrát poznámky',
    chat: 'Přidat přes chat',
    chatPrompt:
      'Pomoz mi přidat poznámku k této schůzce. Zeptej se, co chci zachytit, a před uložením mi ukaž poznámku ke schválení.',
    more: 'Zobrazit další poznámky',
    retry: 'Zkusit znovu',
    loading: 'Načítání poznámek…',
    saved: 'Poznámka uložena',
    views: 'Zobrazení schůzky',
    canvas: 'Pracovní plocha',
    transcript: 'Přepis',
  },
  da: {
    title: 'Dine noter',
    description: 'Fang det vigtige med dine egne ord.',
    access: 'Synlige for alle med adgang til mødet.',
    fromChat: 'Tilføjet via chat',
    unknownAuthor: 'Tidligere medlem',
    removeQuestion: 'Fjern denne note?',
    remove: 'Fjern',
    cancel: 'Annuller',
    loadFailed: 'Noterne kunne ikke indlæses.',
    saveFailed: 'Noten kunne ikke gemmes. Teksten er stadig her; prøv igen.',
    removeFailed: 'Noten kunne ikke fjernes.',
    fileFailed: 'Vælg en .txt- eller .md-fil med højst 12.000 tegn.',
    label: 'Ny note',
    placeholder: 'Idéer, beslutninger, detaljer at huske…',
    shortcut: '⌘ / Ctrl + Enter for at gemme',
    count: '{count} / {max}',
    saving: 'Gemmer…',
    save: 'Gem note',
    add: 'Tilføj en note',
    upload: 'Upload noter',
    chat: 'Tilføj via chat',
    chatPrompt:
      'Hjælp mig med at tilføje en note til mødet. Spørg, hvad jeg vil notere, og vis derefter noten til godkendelse, før du gemmer den.',
    more: 'Vis flere noter',
    retry: 'Prøv igen',
    loading: 'Indlæser noter…',
    saved: 'Note gemt',
    views: 'Mødevisninger',
    canvas: 'Arbejdsflade',
    transcript: 'Transskription',
  },
  de: {
    title: 'Ihre Notizen',
    description: 'Halte das Wichtige in eigenen Worten fest.',
    access: 'Sichtbar für alle, die auf dieses Meeting zugreifen können.',
    fromChat: 'Im Chat hinzugefügt',
    unknownAuthor: 'Ehemaliges Mitglied',
    removeQuestion: 'Diese Notiz entfernen?',
    remove: 'Entfernen',
    cancel: 'Abbrechen',
    loadFailed: 'Die Notizen konnten nicht geladen werden.',
    saveFailed:
      'Ihre Notiz konnte nicht gespeichert werden. Ihr Text ist noch vorhanden. Versuchen Sie es erneut.',
    removeFailed: 'Die Notiz konnte nicht entfernt werden.',
    fileFailed: 'Wählen Sie eine .txt- oder .md-Datei mit höchstens 12.000 Zeichen aus.',
    label: 'Neue Notiz',
    placeholder: 'Ideen, Entscheidungen, wichtige Details…',
    shortcut: '⌘ / Strg + Enter zum Speichern',
    count: '{count} / {max}',
    saving: 'Wird gespeichert…',
    save: 'Notiz speichern',
    add: 'Notiz hinzufügen',
    upload: 'Notizen hochladen',
    chat: 'Im Chat hinzufügen',
    chatPrompt:
      'Hilf mir, diesem Meeting eine Notiz hinzuzufügen. Frage, was ich festhalten möchte, und zeige die Notiz vor dem Speichern zur Bestätigung.',
    more: 'Weitere Notizen anzeigen',
    retry: 'Erneut versuchen',
    loading: 'Notizen werden geladen…',
    saved: 'Notiz gespeichert',
    views: 'Meetingansichten',
    canvas: 'Arbeitsfläche',
    transcript: 'Transkript',
  },
  el: {
    title: 'Οι σημειώσεις σας',
    description: 'Καταγράψτε τα σημαντικά με δικά σας λόγια.',
    access: 'Ορατές σε όλους όσοι έχουν πρόσβαση στη συνάντηση.',
    fromChat: 'Προστέθηκε μέσω συνομιλίας',
    unknownAuthor: 'Πρώην μέλος',
    removeQuestion: 'Να αφαιρεθεί αυτή η σημείωση;',
    remove: 'Αφαίρεση',
    cancel: 'Ακύρωση',
    loadFailed: 'Δεν ήταν δυνατή η φόρτωση των σημειώσεων.',
    saveFailed: 'Δεν ήταν δυνατή η αποθήκευση. Το κείμενό σας παραμένει εδώ· δοκιμάστε ξανά.',
    removeFailed: 'Δεν ήταν δυνατή η αφαίρεση της σημείωσης.',
    fileFailed: 'Επιλέξτε αρχείο .txt ή .md με έως 12.000 χαρακτήρες.',
    label: 'Νέα σημείωση',
    placeholder: 'Ιδέες, αποφάσεις, λεπτομέρειες που αξίζει να θυμάστε…',
    shortcut: '⌘ / Ctrl + Enter για αποθήκευση',
    count: '{count} / {max}',
    saving: 'Αποθήκευση…',
    save: 'Αποθήκευση σημείωσης',
    add: 'Προσθήκη σημείωσης',
    upload: 'Μεταφόρτωση σημειώσεων',
    chat: 'Προσθήκη μέσω συνομιλίας',
    chatPrompt:
      'Βοήθησέ με να προσθέσω μια σημείωση στη συνάντηση. Ρώτησέ με τι θέλω να καταγράψω και δείξε τη σημείωση για έγκριση πριν την αποθηκεύσεις.',
    more: 'Περισσότερες σημειώσεις',
    retry: 'Δοκιμάστε ξανά',
    loading: 'Φόρτωση σημειώσεων…',
    saved: 'Η σημείωση αποθηκεύτηκε',
    views: 'Προβολές συνάντησης',
    canvas: 'Χώρος εργασίας',
    transcript: 'Απομαγνητοφώνηση',
  },
  es: {
    title: 'Tus notas',
    description: 'Recoge lo importante con tus propias palabras.',
    access: 'Visibles para todas las personas con acceso a esta reunión.',
    fromChat: 'Añadida desde el chat',
    unknownAuthor: 'Antiguo miembro',
    removeQuestion: '¿Eliminar esta nota?',
    remove: 'Eliminar',
    cancel: 'Cancelar',
    loadFailed: 'No se pudieron cargar las notas.',
    saveFailed: 'No se pudo guardar tu nota. El texto sigue aquí; inténtalo de nuevo.',
    removeFailed: 'No se pudo eliminar la nota.',
    fileFailed: 'Elige un archivo .txt o .md de hasta 12.000 caracteres.',
    label: 'Nueva nota',
    placeholder: 'Ideas, decisiones, detalles para recordar…',
    shortcut: '⌘ / Ctrl + Intro para guardar',
    count: '{count} / {max}',
    saving: 'Guardando…',
    save: 'Guardar nota',
    add: 'Añadir una nota',
    upload: 'Subir notas',
    chat: 'Añadir desde el chat',
    chatPrompt:
      'Ayúdame a añadir una nota a esta reunión. Pregúntame qué quiero recoger y muéstrame la nota para aprobarla antes de guardarla.',
    more: 'Mostrar más notas',
    retry: 'Volver a intentar',
    loading: 'Cargando notas…',
    saved: 'Nota guardada',
    views: 'Vistas de la reunión',
    canvas: 'Espacio de trabajo',
    transcript: 'Transcripción',
  },
  et: {
    title: 'Sinu märkmed',
    description: 'Pane oluline kirja oma sõnadega.',
    access: 'Nähtavad kõigile, kellel on sellele koosolekule juurdepääs.',
    fromChat: 'Lisatud vestluse kaudu',
    unknownAuthor: 'Endine liige',
    removeQuestion: 'Kas eemaldada see märge?',
    remove: 'Eemalda',
    cancel: 'Tühista',
    loadFailed: 'Märkmeid ei saanud laadida.',
    saveFailed: 'Märget ei saanud salvestada. Tekst on alles; proovi uuesti.',
    removeFailed: 'Märget ei saanud eemaldada.',
    fileFailed: 'Vali .txt- või .md-fail, milles on kuni 12 000 märki.',
    label: 'Uus märge',
    placeholder: 'Ideed, otsused, üksikasjad meelespidamiseks…',
    shortcut: '⌘ / Ctrl + Enter salvestamiseks',
    count: '{count} / {max}',
    saving: 'Salvestamine…',
    save: 'Salvesta märge',
    add: 'Lisa märge',
    upload: 'Laadi märkmed üles',
    chat: 'Lisa vestluse kaudu',
    chatPrompt:
      'Aita mul sellele koosolekule märge lisada. Küsi, mida soovin kirja panna, ja näita märget enne salvestamist kinnitamiseks.',
    more: 'Näita rohkem märkmeid',
    retry: 'Proovi uuesti',
    loading: 'Märkmete laadimine…',
    saved: 'Märge salvestatud',
    views: 'Koosoleku vaated',
    canvas: 'Tööala',
    transcript: 'Transkriptsioon',
  },
  fi: {
    title: 'Omat muistiinpanot',
    description: 'Kirjaa tärkeät asiat omin sanoin.',
    access: 'Näkyvät kaikille, joilla on pääsy kokoukseen.',
    fromChat: 'Lisätty chatissa',
    unknownAuthor: 'Entinen jäsen',
    removeQuestion: 'Poistetaanko muistiinpano?',
    remove: 'Poista',
    cancel: 'Peruuta',
    loadFailed: 'Muistiinpanoja ei voitu ladata.',
    saveFailed: 'Muistiinpanoa ei voitu tallentaa. Tekstisi on tallella; yritä uudelleen.',
    removeFailed: 'Muistiinpanoa ei voitu poistaa.',
    fileFailed: 'Valitse .txt- tai .md-tiedosto, jossa on enintään 12 000 merkkiä.',
    label: 'Uusi muistiinpano',
    placeholder: 'Ideoita, päätöksiä, muistettavia yksityiskohtia…',
    shortcut: '⌘ / Ctrl + Enter tallentaa',
    count: '{count} / {max}',
    saving: 'Tallennetaan…',
    save: 'Tallenna muistiinpano',
    add: 'Lisää muistiinpano',
    upload: 'Lataa muistiinpanot',
    chat: 'Lisää chatissa',
    chatPrompt:
      'Auta minua lisäämään muistiinpano tähän kokoukseen. Kysy, mitä haluan kirjata, ja näytä muistiinpano hyväksyttäväksi ennen tallennusta.',
    more: 'Näytä lisää muistiinpanoja',
    retry: 'Yritä uudelleen',
    loading: 'Ladataan muistiinpanoja…',
    saved: 'Muistiinpano tallennettu',
    views: 'Kokousnäkymät',
    canvas: 'Työtila',
    transcript: 'Litterointi',
  },
  fr: {
    title: 'Vos notes',
    description: 'Retenez l’essentiel avec vos propres mots.',
    access: 'Visibles par toutes les personnes ayant accès à cette réunion.',
    fromChat: 'Ajoutée via le chat',
    unknownAuthor: 'Ancien membre',
    removeQuestion: 'Supprimer cette note ?',
    remove: 'Supprimer',
    cancel: 'Annuler',
    loadFailed: 'Impossible de charger les notes.',
    saveFailed: 'Impossible d’enregistrer la note. Votre texte est conservé ; réessayez.',
    removeFailed: 'Impossible de supprimer la note.',
    fileFailed: 'Choisissez un fichier .txt ou .md de 12 000 caractères maximum.',
    label: 'Nouvelle note',
    placeholder: 'Idées, décisions, détails à retenir…',
    shortcut: '⌘ / Ctrl + Entrée pour enregistrer',
    count: '{count} / {max}',
    saving: 'Enregistrement…',
    save: 'Enregistrer la note',
    add: 'Ajouter une note',
    upload: 'Importer des notes',
    chat: 'Ajouter via le chat',
    chatPrompt:
      'Aidez-moi à ajouter une note à cette réunion. Demandez-moi ce que je souhaite noter, puis présentez la note pour validation avant de l’enregistrer.',
    more: 'Afficher plus de notes',
    retry: 'Réessayer',
    loading: 'Chargement des notes…',
    saved: 'Note enregistrée',
    views: 'Vues de la réunion',
    canvas: 'Espace de travail',
    transcript: 'Transcription',
  },
  ga: {
    title: 'Do chuid nótaí',
    description: 'Breac síos na rudaí tábhachtacha i d’fhocail féin.',
    access: 'Infheicthe ag gach duine a bhfuil rochtain acu ar an gcruinniú seo.',
    fromChat: 'Curtha leis sa chomhrá',
    unknownAuthor: 'Iarchomhalta',
    removeQuestion: 'Bain an nóta seo?',
    remove: 'Bain',
    cancel: 'Cealaigh',
    loadFailed: 'Níorbh fhéidir na nótaí a luchtú.',
    saveFailed: 'Níorbh fhéidir an nóta a shábháil. Tá do théacs anseo fós; bain triail eile as.',
    removeFailed: 'Níorbh fhéidir an nóta a bhaint.',
    fileFailed: 'Roghnaigh comhad .txt nó .md le suas le 12,000 carachtar.',
    label: 'Nóta nua',
    placeholder: 'Smaointe, cinntí, sonraí le cuimhneamh orthu…',
    shortcut: '⌘ / Ctrl + Enter le sábháil',
    count: '{count} / {max}',
    saving: 'Á shábháil…',
    save: 'Sábháil nóta',
    add: 'Cuir nóta leis',
    upload: 'Uaslódáil nótaí',
    chat: 'Cuir leis sa chomhrá',
    chatPrompt:
      'Cabhraigh liom nóta a chur leis an gcruinniú seo. Fiafraigh cad ba mhaith liom a thaifeadadh, ansin taispeáin an nóta dom lena cheadú sula sábhálann tú é.',
    more: 'Taispeáin tuilleadh nótaí',
    retry: 'Bain triail eile as',
    loading: 'Nótaí á luchtú…',
    saved: 'Nóta sábháilte',
    views: 'Amhairc an chruinnithe',
    canvas: 'Spás oibre',
    transcript: 'Tras-scríbhinn',
  },
  hr: {
    title: 'Vaše bilješke',
    description: 'Zabilježite važno vlastitim riječima.',
    access: 'Vidljive svima koji imaju pristup ovom sastanku.',
    fromChat: 'Dodano putem chata',
    unknownAuthor: 'Bivši član',
    removeQuestion: 'Ukloniti ovu bilješku?',
    remove: 'Ukloni',
    cancel: 'Odustani',
    loadFailed: 'Bilješke se nisu mogle učitati.',
    saveFailed: 'Bilješka se nije mogla spremiti. Tekst je još ovdje; pokušajte ponovno.',
    removeFailed: 'Bilješka se nije mogla ukloniti.',
    fileFailed: 'Odaberite .txt ili .md datoteku s najviše 12.000 znakova.',
    label: 'Nova bilješka',
    placeholder: 'Ideje, odluke, pojedinosti za pamćenje…',
    shortcut: '⌘ / Ctrl + Enter za spremanje',
    count: '{count} / {max}',
    saving: 'Spremanje…',
    save: 'Spremi bilješku',
    add: 'Dodaj bilješku',
    upload: 'Učitaj bilješke',
    chat: 'Dodaj putem chata',
    chatPrompt:
      'Pomozi mi dodati bilješku ovom sastanku. Pitaj što želim zabilježiti, a zatim mi pokaži bilješku na odobrenje prije spremanja.',
    more: 'Prikaži više bilježaka',
    retry: 'Pokušaj ponovno',
    loading: 'Učitavanje bilježaka…',
    saved: 'Bilješka spremljena',
    views: 'Prikazi sastanka',
    canvas: 'Radna površina',
    transcript: 'Transkript',
  },
  hu: {
    title: 'Saját jegyzetek',
    description: 'Rögzítse a fontos dolgokat saját szavaival.',
    access: 'Mindenki láthatja, akinek hozzáférése van a megbeszéléshez.',
    fromChat: 'Csevegésben hozzáadva',
    unknownAuthor: 'Korábbi tag',
    removeQuestion: 'Eltávolítja ezt a jegyzetet?',
    remove: 'Eltávolítás',
    cancel: 'Mégse',
    loadFailed: 'A jegyzetek nem tölthetők be.',
    saveFailed: 'A jegyzet nem menthető. A szöveg megmaradt; próbálja újra.',
    removeFailed: 'A jegyzet nem távolítható el.',
    fileFailed: 'Válasszon legfeljebb 12 000 karakteres .txt vagy .md fájlt.',
    label: 'Új jegyzet',
    placeholder: 'Ötletek, döntések, megjegyzendő részletek…',
    shortcut: '⌘ / Ctrl + Enter a mentéshez',
    count: '{count} / {max}',
    saving: 'Mentés…',
    save: 'Jegyzet mentése',
    add: 'Jegyzet hozzáadása',
    upload: 'Jegyzetek feltöltése',
    chat: 'Hozzáadás csevegésben',
    chatPrompt:
      'Segíts jegyzetet hozzáadni ehhez a megbeszéléshez. Kérdezd meg, mit szeretnék rögzíteni, majd mentés előtt mutasd meg jóváhagyásra.',
    more: 'További jegyzetek',
    retry: 'Újrapróbálás',
    loading: 'Jegyzetek betöltése…',
    saved: 'Jegyzet mentve',
    views: 'Megbeszélés nézetei',
    canvas: 'Munkafelület',
    transcript: 'Átirat',
  },
  it: {
    title: 'Le tue note',
    description: 'Annota ciò che conta con parole tue.',
    access: 'Visibili a chiunque abbia accesso a questa riunione.',
    fromChat: 'Aggiunta tramite chat',
    unknownAuthor: 'Ex membro',
    removeQuestion: 'Rimuovere questa nota?',
    remove: 'Rimuovi',
    cancel: 'Annulla',
    loadFailed: 'Impossibile caricare le note.',
    saveFailed: 'Impossibile salvare la nota. Il testo è ancora qui; riprova.',
    removeFailed: 'Impossibile rimuovere la nota.',
    fileFailed: 'Scegli un file .txt o .md con un massimo di 12.000 caratteri.',
    label: 'Nuova nota',
    placeholder: 'Idee, decisioni, dettagli da ricordare…',
    shortcut: '⌘ / Ctrl + Invio per salvare',
    count: '{count} / {max}',
    saving: 'Salvataggio…',
    save: 'Salva nota',
    add: 'Aggiungi una nota',
    upload: 'Carica note',
    chat: 'Aggiungi tramite chat',
    chatPrompt:
      'Aiutami ad aggiungere una nota a questa riunione. Chiedimi cosa voglio annotare, poi mostrami la nota da approvare prima di salvarla.',
    more: 'Mostra altre note',
    retry: 'Riprova',
    loading: 'Caricamento note…',
    saved: 'Nota salvata',
    views: 'Viste della riunione',
    canvas: 'Area di lavoro',
    transcript: 'Trascrizione',
  },
  lt: {
    title: 'Jūsų pastabos',
    description: 'Užrašykite tai, kas svarbu, savo žodžiais.',
    access: 'Matomos visiems, turintiems prieigą prie šio susitikimo.',
    fromChat: 'Pridėta pokalbyje',
    unknownAuthor: 'Buvęs narys',
    removeQuestion: 'Pašalinti šią pastabą?',
    remove: 'Pašalinti',
    cancel: 'Atšaukti',
    loadFailed: 'Nepavyko įkelti pastabų.',
    saveFailed: 'Nepavyko išsaugoti pastabos. Tekstas liko čia; bandykite dar kartą.',
    removeFailed: 'Nepavyko pašalinti pastabos.',
    fileFailed: 'Pasirinkite .txt arba .md failą, kuriame yra iki 12 000 simbolių.',
    label: 'Nauja pastaba',
    placeholder: 'Idėjos, sprendimai, detalės, kurias verta prisiminti…',
    shortcut: '⌘ / Ctrl + Enter išsaugoti',
    count: '{count} / {max}',
    saving: 'Išsaugoma…',
    save: 'Išsaugoti pastabą',
    add: 'Pridėti pastabą',
    upload: 'Įkelti pastabas',
    chat: 'Pridėti pokalbyje',
    chatPrompt:
      'Padėk pridėti pastabą prie šio susitikimo. Paklausk, ką noriu užrašyti, ir prieš išsaugodamas parodyk pastabą patvirtinti.',
    more: 'Rodyti daugiau pastabų',
    retry: 'Bandyti dar kartą',
    loading: 'Įkeliamos pastabos…',
    saved: 'Pastaba išsaugota',
    views: 'Susitikimo rodiniai',
    canvas: 'Darbo sritis',
    transcript: 'Transkripcija',
  },
  lv: {
    title: 'Jūsu piezīmes',
    description: 'Pierakstiet svarīgo saviem vārdiem.',
    access: 'Redzamas visiem, kam ir piekļuve šai sapulcei.',
    fromChat: 'Pievienota tērzētavā',
    unknownAuthor: 'Bijušais dalībnieks',
    removeQuestion: 'Vai noņemt šo piezīmi?',
    remove: 'Noņemt',
    cancel: 'Atcelt',
    loadFailed: 'Piezīmes neizdevās ielādēt.',
    saveFailed: 'Piezīmi neizdevās saglabāt. Teksts ir saglabājies; mēģiniet vēlreiz.',
    removeFailed: 'Piezīmi neizdevās noņemt.',
    fileFailed: 'Izvēlieties .txt vai .md failu ar ne vairāk kā 12 000 rakstzīmēm.',
    label: 'Jauna piezīme',
    placeholder: 'Idejas, lēmumi, detaļas, ko atcerēties…',
    shortcut: '⌘ / Ctrl + Enter, lai saglabātu',
    count: '{count} / {max}',
    saving: 'Saglabā…',
    save: 'Saglabāt piezīmi',
    add: 'Pievienot piezīmi',
    upload: 'Augšupielādēt piezīmes',
    chat: 'Pievienot tērzētavā',
    chatPrompt:
      'Palīdzi man pievienot piezīmi šai sapulcei. Pajautā, ko vēlos pierakstīt, un pirms saglabāšanas parādi piezīmi apstiprināšanai.',
    more: 'Rādīt vairāk piezīmju',
    retry: 'Mēģināt vēlreiz',
    loading: 'Ielādē piezīmes…',
    saved: 'Piezīme saglabāta',
    views: 'Sapulces skati',
    canvas: 'Darba laukums',
    transcript: 'Transkripts',
  },
  mt: {
    title: 'In-noti tiegħek',
    description: 'Ikteb dak li jgħodd, fi kliemek.',
    access: 'Viżibbli għal kull min għandu aċċess għal din il-laqgħa.',
    fromChat: 'Miżjuda permezz taċ-chat',
    unknownAuthor: 'Membru preċedenti',
    removeQuestion: 'Tneħħi din in-nota?',
    remove: 'Neħħi',
    cancel: 'Ikkanċella',
    loadFailed: 'In-noti ma setgħux jitgħabbew.',
    saveFailed: 'In-nota ma setgħetx tiġi ssejvjata. It-test għadu hawn; erġa’ pprova.',
    removeFailed: 'In-nota ma setgħetx titneħħa.',
    fileFailed: 'Agħżel fajl .txt jew .md b’sa 12,000 karattru.',
    label: 'Nota ġdida',
    placeholder: 'Ideat, deċiżjonijiet, dettalji biex tiftakar…',
    shortcut: '⌘ / Ctrl + Enter biex tissejvja',
    count: '{count} / {max}',
    saving: 'Qed tissejvja…',
    save: 'Issejvja n-nota',
    add: 'Żid nota',
    upload: 'Tella’ noti',
    chat: 'Żid permezz taċ-chat',
    chatPrompt:
      'Għinni nżid nota ma’ din il-laqgħa. Staqsini x’irrid nikteb, imbagħad urini n-nota għall-approvazzjoni qabel ma tissejvjaha.',
    more: 'Uri aktar noti',
    retry: 'Erġa’ pprova',
    loading: 'Qed jitgħabbew in-noti…',
    saved: 'Nota ssejvjata',
    views: 'Veduti tal-laqgħa',
    canvas: 'Spazju tax-xogħol',
    transcript: 'Traskrizzjoni',
  },
  nl: {
    title: 'Je notities',
    description: 'Leg het belangrijkste vast in je eigen woorden.',
    access: 'Zichtbaar voor iedereen met toegang tot deze vergadering.',
    fromChat: 'Toegevoegd via chat',
    unknownAuthor: 'Voormalig lid',
    removeQuestion: 'Deze notitie verwijderen?',
    remove: 'Verwijderen',
    cancel: 'Annuleren',
    loadFailed: 'De notities konden niet worden geladen.',
    saveFailed:
      'Je notitie kon niet worden opgeslagen. Je tekst blijft bewaard; probeer het opnieuw.',
    removeFailed: 'De notitie kon niet worden verwijderd.',
    fileFailed: 'Kies een .txt- of .md-bestand met maximaal 12.000 tekens.',
    label: 'Nieuwe notitie',
    placeholder: 'Ideeën, besluiten, details om te onthouden…',
    shortcut: '⌘ / Ctrl + Enter om op te slaan',
    count: '{count} / {max}',
    saving: 'Opslaan…',
    save: 'Notitie opslaan',
    add: 'Notitie toevoegen',
    upload: 'Notities uploaden',
    chat: 'Toevoegen via chat',
    chatPrompt:
      'Help me een notitie aan deze vergadering toe te voegen. Vraag wat ik wil vastleggen en toon de notitie ter goedkeuring voordat je deze opslaat.',
    more: 'Meer notities tonen',
    retry: 'Opnieuw proberen',
    loading: 'Notities laden…',
    saved: 'Notitie opgeslagen',
    views: 'Vergaderingsweergaven',
    canvas: 'Werkruimte',
    transcript: 'Transcript',
  },
  pl: {
    title: 'Twoje notatki',
    description: 'Zapisz to, co ważne, własnymi słowami.',
    access: 'Widoczne dla wszystkich osób z dostępem do tego spotkania.',
    fromChat: 'Dodano przez czat',
    unknownAuthor: 'Były członek',
    removeQuestion: 'Usunąć tę notatkę?',
    remove: 'Usuń',
    cancel: 'Anuluj',
    loadFailed: 'Nie udało się wczytać notatek.',
    saveFailed: 'Nie udało się zapisać notatki. Tekst jest zachowany; spróbuj ponownie.',
    removeFailed: 'Nie udało się usunąć notatki.',
    fileFailed: 'Wybierz plik .txt lub .md zawierający do 12 000 znaków.',
    label: 'Nowa notatka',
    placeholder: 'Pomysły, decyzje, szczegóły do zapamiętania…',
    shortcut: '⌘ / Ctrl + Enter, aby zapisać',
    count: '{count} / {max}',
    saving: 'Zapisywanie…',
    save: 'Zapisz notatkę',
    add: 'Dodaj notatkę',
    upload: 'Prześlij notatki',
    chat: 'Dodaj przez czat',
    chatPrompt:
      'Pomóż mi dodać notatkę do tego spotkania. Zapytaj, co chcę zapisać, a następnie pokaż notatkę do zatwierdzenia przed zapisaniem.',
    more: 'Pokaż więcej notatek',
    retry: 'Spróbuj ponownie',
    loading: 'Wczytywanie notatek…',
    saved: 'Notatka zapisana',
    views: 'Widoki spotkania',
    canvas: 'Obszar roboczy',
    transcript: 'Transkrypcja',
  },
  pt: {
    title: 'As suas notas',
    description: 'Registe o essencial com as suas próprias palavras.',
    access: 'Visíveis para todas as pessoas com acesso a esta reunião.',
    fromChat: 'Adicionada pelo chat',
    unknownAuthor: 'Antigo membro',
    removeQuestion: 'Remover esta nota?',
    remove: 'Remover',
    cancel: 'Cancelar',
    loadFailed: 'Não foi possível carregar as notas.',
    saveFailed: 'Não foi possível guardar a nota. O texto continua aqui; tente novamente.',
    removeFailed: 'Não foi possível remover a nota.',
    fileFailed: 'Escolha um ficheiro .txt ou .md com até 12 000 caracteres.',
    label: 'Nova nota',
    placeholder: 'Ideias, decisões, detalhes a recordar…',
    shortcut: '⌘ / Ctrl + Enter para guardar',
    count: '{count} / {max}',
    saving: 'A guardar…',
    save: 'Guardar nota',
    add: 'Adicionar nota',
    upload: 'Carregar notas',
    chat: 'Adicionar pelo chat',
    chatPrompt:
      'Ajude-me a adicionar uma nota a esta reunião. Pergunte o que quero registar e mostre a nota para aprovação antes de a guardar.',
    more: 'Mostrar mais notas',
    retry: 'Tentar novamente',
    loading: 'A carregar notas…',
    saved: 'Nota guardada',
    views: 'Vistas da reunião',
    canvas: 'Área de trabalho',
    transcript: 'Transcrição',
  },
  ro: {
    title: 'Notițele tale',
    description: 'Notează ce contează, cu propriile cuvinte.',
    access: 'Vizibile tuturor celor care au acces la această întâlnire.',
    fromChat: 'Adăugată prin chat',
    unknownAuthor: 'Fost membru',
    removeQuestion: 'Elimini această notiță?',
    remove: 'Elimină',
    cancel: 'Anulează',
    loadFailed: 'Notițele nu au putut fi încărcate.',
    saveFailed: 'Notița nu a putut fi salvată. Textul este încă aici; încearcă din nou.',
    removeFailed: 'Notița nu a putut fi eliminată.',
    fileFailed: 'Alege un fișier .txt sau .md cu maximum 12.000 de caractere.',
    label: 'Notiță nouă',
    placeholder: 'Idei, decizii, detalii de reținut…',
    shortcut: '⌘ / Ctrl + Enter pentru salvare',
    count: '{count} / {max}',
    saving: 'Se salvează…',
    save: 'Salvează notița',
    add: 'Adaugă o notiță',
    upload: 'Încarcă notițe',
    chat: 'Adaugă prin chat',
    chatPrompt:
      'Ajută-mă să adaug o notiță la această întâlnire. Întreabă ce vreau să notez, apoi arată-mi notița pentru aprobare înainte de a o salva.',
    more: 'Arată mai multe notițe',
    retry: 'Încearcă din nou',
    loading: 'Se încarcă notițele…',
    saved: 'Notiță salvată',
    views: 'Vizualizări ale întâlnirii',
    canvas: 'Spațiu de lucru',
    transcript: 'Transcriere',
  },
  sk: {
    title: 'Vaše poznámky',
    description: 'Zachyťte to dôležité vlastnými slovami.',
    access: 'Viditeľné pre všetkých s prístupom k tomuto stretnutiu.',
    fromChat: 'Pridané cez chat',
    unknownAuthor: 'Bývalý člen',
    removeQuestion: 'Odstrániť túto poznámku?',
    remove: 'Odstrániť',
    cancel: 'Zrušiť',
    loadFailed: 'Poznámky sa nepodarilo načítať.',
    saveFailed: 'Poznámku sa nepodarilo uložiť. Text zostáva zachovaný; skúste to znova.',
    removeFailed: 'Poznámku sa nepodarilo odstrániť.',
    fileFailed: 'Vyberte súbor .txt alebo .md s najviac 12 000 znakmi.',
    label: 'Nová poznámka',
    placeholder: 'Nápady, rozhodnutia, detaily na zapamätanie…',
    shortcut: '⌘ / Ctrl + Enter na uloženie',
    count: '{count} / {max}',
    saving: 'Ukladanie…',
    save: 'Uložiť poznámku',
    add: 'Pridať poznámku',
    upload: 'Nahrať poznámky',
    chat: 'Pridať cez chat',
    chatPrompt:
      'Pomôž mi pridať poznámku k tomuto stretnutiu. Spýtaj sa, čo chcem zachytiť, a pred uložením mi ukáž poznámku na schválenie.',
    more: 'Zobraziť ďalšie poznámky',
    retry: 'Skúsiť znova',
    loading: 'Načítavanie poznámok…',
    saved: 'Poznámka uložená',
    views: 'Zobrazenia stretnutia',
    canvas: 'Pracovná plocha',
    transcript: 'Prepis',
  },
  sl: {
    title: 'Vaši zapiski',
    description: 'Zabeležite pomembno s svojimi besedami.',
    access: 'Vidni vsem, ki imajo dostop do tega sestanka.',
    fromChat: 'Dodano prek klepeta',
    unknownAuthor: 'Nekdanji član',
    removeQuestion: 'Odstranim ta zapisek?',
    remove: 'Odstrani',
    cancel: 'Prekliči',
    loadFailed: 'Zapiskov ni bilo mogoče naložiti.',
    saveFailed: 'Zapiska ni bilo mogoče shraniti. Besedilo je še tukaj; poskusite znova.',
    removeFailed: 'Zapiska ni bilo mogoče odstraniti.',
    fileFailed: 'Izberite datoteko .txt ali .md z največ 12.000 znaki.',
    label: 'Nov zapisek',
    placeholder: 'Ideje, odločitve, podrobnosti za pozneje…',
    shortcut: '⌘ / Ctrl + Enter za shranjevanje',
    count: '{count} / {max}',
    saving: 'Shranjevanje…',
    save: 'Shrani zapisek',
    add: 'Dodaj zapisek',
    upload: 'Naloži zapiske',
    chat: 'Dodaj prek klepeta',
    chatPrompt:
      'Pomagaj mi dodati zapisek k temu sestanku. Vprašaj, kaj želim zapisati, nato mi pokaži zapisek v odobritev pred shranjevanjem.',
    more: 'Prikaži več zapiskov',
    retry: 'Poskusi znova',
    loading: 'Nalaganje zapiskov…',
    saved: 'Zapisek shranjen',
    views: 'Pogledi sestanka',
    canvas: 'Delovna površina',
    transcript: 'Prepis',
  },
  sv: {
    title: 'Dina anteckningar',
    description: 'Fånga det viktiga, med egna ord.',
    access: 'Synliga för alla som har tillgång till mötet.',
    fromChat: 'Tillagd via chatten',
    unknownAuthor: 'Tidigare medlem',
    removeQuestion: 'Ta bort anteckningen?',
    remove: 'Ta bort',
    cancel: 'Avbryt',
    loadFailed: 'Anteckningarna kunde inte hämtas.',
    saveFailed: 'Anteckningen kunde inte sparas. Texten finns kvar; försök igen.',
    removeFailed: 'Anteckningen kunde inte tas bort.',
    fileFailed: 'Välj en .txt- eller .md-fil med högst 12 000 tecken.',
    label: 'Ny anteckning',
    placeholder: 'Idéer, beslut, detaljer att minnas…',
    shortcut: '⌘ / Ctrl + Enter för att spara',
    count: '{count} / {max}',
    saving: 'Sparar…',
    save: 'Spara anteckning',
    add: 'Lägg till anteckning',
    upload: 'Ladda upp anteckningar',
    chat: 'Lägg till via chatten',
    chatPrompt:
      'Hjälp mig att lägga till en anteckning till mötet. Fråga vad jag vill fånga och visa sedan anteckningen för godkännande innan du sparar den.',
    more: 'Visa fler anteckningar',
    retry: 'Försök igen',
    loading: 'Hämtar anteckningar…',
    saved: 'Anteckningen sparad',
    views: 'Mötesvyer',
    canvas: 'Arbetsyta',
    transcript: 'Transkript',
  },
};
export default copy;
